<template>
  <button
    type="button"
    class="w-full flex items-center gap-2.5 px-3 py-2 border-b border-white/5 hover:bg-white/[0.03] text-left transition-colors"
    :class="active ? 'bg-white/[0.04]' : ''"
    @click="$emit('click')"
  >
    <!-- Group row: show a generic "people" icon bubble (no per-user avatar
         makes sense when N members) + a small member-count badge. -->
    <div v-if="thread.kind === 'group'" class="relative shrink-0 w-9 h-9 rounded-full bg-primary/15 text-primary flex items-center justify-center">
      <el-icon size="16"><UserFilled /></el-icon>
      <span
        class="absolute -bottom-0.5 -right-0.5 min-w-[14px] h-[14px] px-1 rounded-full bg-[var(--surface-color,#0d0e12)] text-[9px] text-white font-bold flex items-center justify-center ring-1 ring-white/10"
      >{{ thread.memberCount || 0 }}</span>
    </div>
    <div v-else class="relative shrink-0">
      <img
        :src="thread.otherUser?.avatar || '/images/default-avatar.svg'"
        :alt="thread.otherUser?.name || 'User'"
        class="w-9 h-9 rounded-full object-cover"
      />
      <span
        v-if="isOnline(thread.otherUser?.id)"
        class="absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full ring-2 ring-[var(--surface-color,#0d0e12)]"
        :class="dotClassFor(getStatus(thread.otherUser?.id))"
      ></span>
    </div>
    <div class="min-w-0 flex-1">
      <div class="flex items-baseline gap-2">
        <span class="text-sm text-white font-semibold truncate">
          {{ thread.kind === 'group' ? (thread.title || 'Group') : (thread.otherUser?.name || 'User') }}
        </span>
        <!-- Tenant badge (SA cross-tenant threads only) -->
        <span
          v-if="thread.tenant"
          class="shrink-0 text-[9px] font-semibold uppercase tracking-wider px-1 rounded bg-primary/10 text-primary border border-primary/30"
          :title="'On tenant: ' + thread.tenant.name"
        >{{ thread.tenant.name }}</span>
        <span v-if="thread.lastMessageAt" class="text-[10px] text-text-dim shrink-0 ml-auto">{{ timeLabel }}</span>
      </div>
      <p class="text-xs text-text-muted truncate">
        <span v-if="thread.lastMessagePreview">{{ thread.lastMessagePreview }}</span>
        <span v-else-if="thread.kind === 'group'" class="italic text-text-dim">{{ thread.memberCount || 0 }} members</span>
        <span v-else class="italic text-text-dim">No messages yet</span>
      </p>
    </div>
    <span
      v-if="thread.unread > 0"
      class="shrink-0 min-w-[18px] h-[18px] px-1 rounded-full bg-primary text-black text-[10px] font-bold flex items-center justify-center"
    >
      {{ thread.unread > 99 ? '99+' : thread.unread }}
    </span>
  </button>
</template>

<script setup>
import { computed } from 'vue'
import { UserFilled } from '@element-plus/icons-vue'
import { usePresence } from '~/composables/chat/usePresence'

const props = defineProps({
  thread: { type: Object, required: true },
  active: { type: Boolean, default: false }
})
defineEmits(['click'])

const { isOnline, getStatus } = usePresence()

function dotClassFor(status) {
  if (status === 'afk') return 'bg-amber-400'
  if (status === 'dnd') return 'bg-red-500'
  return 'bg-emerald-400'
}

const timeLabel = computed(() => {
  if (!props.thread.lastMessageAt) return ''
  const d = new Date(props.thread.lastMessageAt)
  if (isNaN(d.getTime())) return ''
  const now = Date.now()
  const diffMs = now - d.getTime()
  const hour = 60 * 60 * 1000
  const day = 24 * hour
  if (diffMs < hour) {
    const m = Math.max(1, Math.floor(diffMs / 60000))
    return `${m}m`
  }
  if (diffMs < day) return `${Math.floor(diffMs / hour)}h`
  if (diffMs < 7 * day) return `${Math.floor(diffMs / day)}d`
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' })
})
</script>
